// src/api/websiteService.ts
import { apiClient } from './apiClient';

interface WebsiteRaw {
    id: string;
    userId: string;
    url: string;
    domain: string;
    has_llms_txt: boolean;
    llms_txt_content?: string;
    framework?: string;
    retest_enabled: boolean;
    retest_excluded_queries: string[];
    last_retested_at?: string;
    created_at: string;
    updated_at: string;
}

export interface Website {
    id: string;
    userId: string;
    url: string;
    domain: string;
    hasLlmsTxt: boolean;
    llmsTxtContent?: string;
    framework?: string;
    retestEnabled: boolean;
    retestExcludedQueries: string[];
    lastRetestedAt?: string;
    createdAt: string;
    updatedAt: string;
}

const transformWebsite = (raw: WebsiteRaw): Website => ({
    id: raw.id,
    userId: raw.userId,
    url: raw.url,
    domain: raw.domain,
    hasLlmsTxt: raw.has_llms_txt,
    llmsTxtContent: raw.llms_txt_content,
    framework: raw.framework,
    retestEnabled: raw.retest_enabled,
    retestExcludedQueries: raw.retest_excluded_queries || [],
    lastRetestedAt: raw.last_retested_at,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
});

export const websiteService = {
    getUserWebsites: async (): Promise<Website[]> => {
        const response = await apiClient.get<WebsiteRaw[]>('/websites');
        return response.data.map(transformWebsite);
    },

    findOrCreateWebsite: async (url: string): Promise<Website> => {
        const response = await apiClient.post<WebsiteRaw>('/websites/find-or-create', { url });
        return transformWebsite(response.data);
    },
    getWebsite: async (websiteId: string): Promise<Website> => {
        const response = await apiClient.get<WebsiteRaw>(`/websites/${websiteId}`);
        return transformWebsite(response.data);
    },

    // Step 1: Add and examine website (combined)
    addAndExamineWebsite: async (data: { url: string }) => {
        const response = await apiClient.post('/websites/add-and-examine', data);
        return response.data;
    },

    // Step 2: Generate query suggestions
    generateQuerySuggestions: async (data: { websiteId: string; url: string; maxUrlsToAnalyze?: number }) => {
        const response = await apiClient.post(`/websites/${data.websiteId}/generate-queries`, {
            url: data.url,
            maxUrlsToAnalyze: data.maxUrlsToAnalyze
        });
        return response.data;
    },

    // Step 3: Generate and save llms.txt
    generateAndSaveLlmsTxt: async (data: {
        websiteId: string;
        url: string;
        maxUrls?: number;
        customInstructions?: string;
        selectedQueries?: string[];
        customRankingQuery?: string;
    }) => {
        const response = await apiClient.post(`/websites/${data.websiteId}/generate-llms`, data);
        return response.data;
    },

    // Update website
    updateWebsite: async (websiteId: string, data: any) => {
        const response = await apiClient.put(`/websites/${websiteId}`, data);
        return response.data;
    },

    // Verify installation
    verifyInstallation: async (websiteId: string) => {
        const response = await apiClient.post(`/websites/${websiteId}/verify`);
        return response.data;
    },

    // Delete website
    deleteWebsite: async (websiteId: string) => {
        const response = await apiClient.delete(`/websites/${websiteId}`);
        return response.data;
    },

    // Update retest settings
    updateRetestSettings: async (websiteId: string, settings: { retestEnabled?: boolean; excludedQueries?: string[] }): Promise<Website> => {
        const response = await apiClient.patch<WebsiteRaw>(`/websites/${websiteId}/retest-settings`, settings);
        return transformWebsite(response.data);
    },

    // Toggle per-query retest
    toggleQueryRetest: async (websiteId: string, query: string, enabled: boolean): Promise<Website> => {
        const response = await apiClient.patch<WebsiteRaw>(`/websites/${websiteId}/retest-settings/toggle-query`, { query, enabled });
        return transformWebsite(response.data);
    },
};
